/**
 * Login Page Component
 *
 * Provides authentication interface for users to log into the ProMedic
 * Project Management System.
 *
 * @module pages/Login
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { getLandingPathForRole } from '../config/navConfig';
import { resolveLoginIdentifier } from '../utils/loginIdentifier';
import '../styles/login.css';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, isAuthenticated, profile, loading: authLoading } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // #389: SessionExpiryWatcher redirects here with state.sessionExpired=true
  // when the session/token expired or an MCP call came back 401. Read it
  // once on mount so a page refresh (which clears router state) doesn't
  // keep re-showing it.
  const [sessionExpiredMessage] = useState(
    () => (location.state?.sessionExpired
      ? 'Tu sesión expiró, iniciá sesión de nuevo.'
      : null)
  );

  /**
   * Get redirect path based on user role.
   *
   * Honors `location.state.from` first (so a deep link survives a login
   * detour), then falls back to the role-canonical landing page from
   * `getLandingPathForRole`. The mapping lives in `src/config/navConfig.js`
   * so the sidebar and the post-login redirect agree on what each role's
   * "home" is (issue #132).
   *
   * @param {Object} userProfile - User profile object
   * @returns {string} Redirect path
   */
  const getRedirectPath = useCallback((userProfile = profile) => {
    // Check if there's a specific path they were trying to access
    if (location.state?.from?.pathname) {
      return location.state.from.pathname;
    }
    return getLandingPathForRole(userProfile?.role);
  }, [location, profile]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading && profile) {
      const redirectPath = getRedirectPath(profile);
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, authLoading, profile, navigate, location, getRedirectPath]);

  /**
   * Handle input change
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Inline validation — Spanish messages, no browser-native tooltip
    // El campo acepta correo O código de empleado (cuentas sin correo propio):
    // sin '@' se completa con el dominio interno ANTES de signInWithPassword.
    // Ver src/utils/loginIdentifier.js.
    if (!formData.email.trim()) { setError('El correo electrónico o código de empleado es requerido'); return; }
    const { email: loginEmail } = resolveLoginIdentifier(formData.email);
    if (!loginEmail) { setError('Ingresa un correo electrónico válido o tu código de empleado'); return; }
    if (!formData.password) { setError('La contraseña es requerida'); return; }

    setLoading(true);

    try {
      const { success, error } = await signIn(loginEmail, formData.password);

      if (!success) {
        throw error || new Error('Error al iniciar sesión');
      }

      // Redirect handled by useEffect above
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Error al iniciar sesión. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Container>
        <Row className="justify-content-center align-items-center min-vh-100">
          <Col xs={12} sm={10} md={8} lg={6} xl={5}>
            <Card className="shadow-lg login-card">
              <Card.Body className="p-4 p-sm-5">
                <div className="text-center mb-4">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/logos/Logo_PROMEDIC-07.png`}
                    alt="ProMedic Logo"
                    className="login-logo mb-3"
                  />
                  <h2 className="fw-bold text-primary">Sistema de Gestión</h2>
                  <p className="text-muted">Ingresa tus credenciales para continuar</p>
                </div>

                {sessionExpiredMessage && (
                  <Alert variant="warning" data-testid="session-expired-alert">
                    {sessionExpiredMessage}
                  </Alert>
                )}

                {error && (
                  <Alert variant="danger" dismissible onClose={() => setError(null)} data-testid="login-error-alert">
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit} noValidate>
                  <Form.Group className="mb-3" controlId="email">
                    <Form.Label>Correo o código de empleado</Form.Label>
                    <Form.Control
                      type="text"
                      inputMode="email"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      name="email"
                      placeholder="usuario@example.com o tecnico01"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="username"
                      disabled={loading}
                      data-testid="login-email-input"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="password">
                    <Form.Label>Contraseña</Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        autoComplete="current-password"
                        disabled={loading}
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        disabled={loading}
                        data-testid="login-show-password-toggle"
                      >
                        {showPassword ? (
                          <i className="bi bi-eye-slash"></i>
                        ) : (
                          <i className="bi bi-eye"></i>
                        )}
                      </button>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="rememberMe">
                    <Form.Check
                      type="checkbox"
                      name="rememberMe"
                      label="Recordarme"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    className="w-100 py-2 mb-3"
                    disabled={loading || authLoading}
                    data-testid="login-submit-button"
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Iniciando sesión...
                      </>
                    ) : (
                      'Iniciar Sesión'
                    )}
                  </Button>

                  <div className="text-center">
                    <Link to="/forgot-password" className="text-muted small">
                      ¿Olvidaste tu contraseña?
                    </Link>
                  </div>
                </Form>

                <hr className="my-4" />

                <div className="text-center">
                  <Link to="/" className="text-muted small">
                    Volver al sitio principal
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Login;
